'use client'

import { useMemo } from 'react'
import { Box, Button, Chip, Link, Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { appPaths } from '@config'
import {
  type FeaturePlanInfoWithType,
  transformFeatureDataForInfoTable,
} from '@query/page-query/pricing-query/pricing.model'
import {
  SectionHeader,
  SectionWrapper,
  type SectionHeaderData,
} from '@components/common'
import { portalUrl } from '@utils/environment'
import { SCROLL_ID } from '@utils/scroll'
import { commonStyles, getStyles } from '@utils/styles'
import { usePricingFeatures } from 'src/client-query/pricing'
import { usePricingContext } from 'src/context'
import { InfoTable } from './InfoTable'
import defaultStyles from './styles'

export type PricingSectionData = {
  featurePlanInfoWithType: FeaturePlanInfoWithType
}

export type ComparePlansData = {
  sectionHeaderData: SectionHeaderData
  featurePlanInfoWithType: FeaturePlanInfoWithType
}

export function ComparePlans({ sectionHeaderData }: ComparePlansData) {
  const styles = getStyles(defaultStyles)
  const { features } = usePricingFeatures({ autoFetch: true })
  const { dynamicPricingUSDMonthly } = usePricingContext()
  const t = useTranslations('Pricing.comparePlans')

  const isCustomPlan = useMemo(() => {
    return dynamicPricingUSDMonthly?.plan_type === 'ENTERPRISE'
  }, [dynamicPricingUSDMonthly])

  const topHeaderInfo = [
    {
      title: 'Growth',
      pricing: dynamicPricingUSDMonthly?.comparable_plans.growth_plan?.price,
      buttonText: isCustomPlan
        ? 'Contact Sales'
        : t('buttonText', {
            days:
              dynamicPricingUSDMonthly?.comparable_plans.growth_plan
                ?.trial_period ?? 14,
          }),
      isRecommended: false,
      href: `${portalUrl}register`,
    },
    {
      title: 'Premium',
      pricing: dynamicPricingUSDMonthly?.comparable_plans.premium_plan?.price,
      buttonText: isCustomPlan ? 'Contact Sales' : 'Choose Plan',
      isRecommended: true,
      href: appPaths.contactUs,
    },
    {
      title: 'Enterprise',
      pricing: 'Custom',
      buttonText: 'Contact Sales',
      isRecommended: false,
      href: appPaths.contactUs,
    },
  ] as const

  return (
    <SectionWrapper
      useAsStickyWrapper
      csx={{
        wrapper: {
          display: {
            xs: 'none',
            sm: 'flex',
          },
        },
      }}
    >
      <Stack>
        <Stack gap={4} position="relative" id={SCROLL_ID.COMPARE_PLANS}>
          <SectionHeader {...sectionHeaderData} />

          <Box {...styles('tableWrapper')}>
            {/* Premium column highlight overlay */}
            <Box {...styles('premiumColumnHighlight')} />
            <Stack>
              <Box {...styles('tableStickyHeader')}>
                <Typography variant="h6.600">Features</Typography>
                <Stack direction="row" gap={2}>
                  {topHeaderInfo.map((item, index) => (
                    <Stack
                      key={item.title}
                      gap={0.5}
                      justifyContent="space-between"
                      {...styles('planHeaderItem', {
                        marginLeft:
                          index === 0
                            ? {
                                xs: '12px',
                                sm: '0',
                              }
                            : 'unset',
                        scale: {
                          xs: 0.7,
                          sm: 1,
                        },
                        ...(!item.title.includes('Premium') && {
                          border: {
                            xs: 'none',
                            sm: '1px solid',
                          },
                          borderColor: {
                            xs: 'unset',
                            sm: 'border.dark',
                          },
                          borderRadius: '8px',
                        }),
                        // Highlight Premium header
                        ...(item.title.includes('Premium') &&
                          styles('premiumPlanHeader').sx),
                      })}
                    >
                      <Box sx={{ zIndex: 2 }}>
                        <Box
                          sx={{
                            ...commonStyles.flexSpace(),
                            flexDirection: {
                              xs: 'column',
                              sm: 'row',
                            },
                          }}
                        >
                          <Typography
                            variant="h6.600"
                            textAlign={{
                              xs: 'start',
                              sm: 'center',
                            }}
                          >
                            {item.title}{' '}
                          </Typography>
                          {item.isRecommended && (
                            <Chip
                              variant="trans"
                              label="✦ Recommended"
                              color="primary"
                              {...styles('recommendedChip')}
                            />
                          )}
                        </Box>
                        {typeof item.pricing === 'number' ? (
                          <Box mt={1}>
                            <Stack
                              direction={{
                                xs: 'column',
                                sm: 'row',
                              }}
                              gap={0.5}
                            >
                              <Typography variant="h5.600">
                                ${item.pricing}
                              </Typography>
                              <Stack alignItems="baseline" justifyContent="end">
                                <Typography
                                  variant="body3.500"
                                  color="text.secondary"
                                  whiteSpace={{
                                    xs: 'wrap',
                                    sm: 'nowrap',
                                  }}
                                >
                                  USD per month
                                </Typography>
                                <Typography
                                  variant="caption1.500"
                                  color="text.secondary"
                                >
                                  Billed Monthly
                                </Typography>
                              </Stack>
                            </Stack>
                          </Box>
                        ) : (
                          <Typography variant="h5.600" component="div">
                            Custom
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        sx={{
                          marginTop: '16px',
                          width: '100%',
                          zIndex: 2,
                          ...commonStyles.hideElementOnMobile(true),
                        }}
                        component={Link}
                        href={item.href}
                        target="_blank"
                        variant={
                          item.title.includes('Premium')
                            ? 'contained'
                            : 'outlined'
                        }
                      >
                        {item.buttonText}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </Box>
              {features &&
                transformFeatureDataForInfoTable(features).map(
                  ({ category, data }, index) => (
                    <InfoTable
                      key={category}
                      title={category}
                      data={data}
                      index={index}
                    />
                  )
                )}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </SectionWrapper>
  )
}
